// MMEC_APP_SCRATCH_v3 — All prop errors resolved
// Wires MMECShell (advanced) and MMECSimpleShell (simple) around all existing tools.

import React, {
    Suspense,
    lazy,
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from "react";

// ── Contexts ─────────────────────────────────────────────────────────────────
import { useAuth } from "./contexts/AuthContext";
import { useLanguage } from "./contexts/LanguageContext";
import { useSymbolMapping } from "./contexts/SymbolMappingContext";
import { useTradingCall } from "./contexts/TradingCallContext";
import { useDataSource } from "./contexts/DataSourceContext";
import { useExperienceMode } from "./contexts/ExperienceModeContext";
import { useMMECTheme } from "./contexts/MMECThemeContext";

// ── Utils & Services ──────────────────────────────────────────────────────────
import { isMmecToolsRailUser } from "./utils/adminUtils";
import { usePageId } from "./utils/pageIdUtils";
import { dataService } from "./services/dataService";
import {
    getPriorityFetchSymbols,
    readStablePrimarySessionFirst,
    readStableUnderlyingsSessionFirst,
    setStableTabSelection,
} from "./utils/stableTabStorage";
import { useRegime } from "./contexts/RegimeContext";
import { ScannerData } from "./types/ScannerData";
import { shouldFeedLiveMarketData } from "./utils/marketSession";
import {
    getAnalyticsHubAiSnapshot,
    subscribeAnalyticsHubAiSnapshot,
} from "./utils/analyticsAiBridge";

// ── MMEC Shells ───────────────────────────────────────────────────────────────
import MMECShell from "./components/mmec/MMECShell";
import MMECSimpleShell from "./components/mmec/MMECSimpleShell";
import RtlScannerView from "./components/RtlScannerView";
import FlowTapeView from "./components/FlowTapeView";
import FlowMatrixView from "./components/FlowMatrixView";
import AlertToasts from "./components/alerts/AlertToasts";
import StableFetchAlerts from "./components/alerts/StableFetchAlerts";

// ── Auth / Legal ──────────────────────────────────────────────────────────────
import LoginScreen from "./components/LoginScreen";
import LegalDisclaimer from "./components/LegalDisclaimer";

// ── Tool Components ───────────────────────────────────────────────────────────
import DataTable from "./components/DataTable";
import FlowHeatmap from "./components/FlowHeatmap";
import { TAB_REDIRECTS } from "./utils/tabManager";
import { canUseProtect, userAllowedForProtect } from "./utils/protectAccess";
import MarketAnalysisPage from "./components/analysis/MarketAnalysisPage";
import VolumeOISpotGammaChart from "./components/charts/VolumeOISpotGammaChart";
import VerticalSpotGammaVsOIChart from "./components/charts/VerticalSpotGammaVsOIChart";
import OptionChangeTrackerPage from "./components/OptionChangeTrackerPage";
import RealTimeGexChart from "./components/RealTimeGexChart";
import LiveGexWorkspace, { LiveGexBoundary } from "./components/livegex/LiveGexWorkspace";
import TradesChartReformed from "./components/TradesChartReformed";
import VolumeDeltaReformed from "./components/VolumeDeltaReformed";
import TradingCallSystem from "./components/TradingCallSystem";
import TradingCallTracker from "./components/TradingCallTracker";
import TradingCallsWidget from "./components/charts/TradingCallsWidget";
import TradingCallGeneratorPage from "./components/TradingCallGeneratorPage";
import PostedCallsFeed from "./components/PostedCallsFeed";
import SigmaRangeCalculator from "./components/tools/SigmaRangeCalculator";
import VirtualTour from "./components/VirtualTour";
import CompanionTourLauncher from "./components/CompanionTourLauncher";
import MMECCompanion from "./components/ai-chatbot/MMECCompanion";
import TrackedContractsPanel from "./components/TrackedContractsPanel";
import TrackedContractsView from "./components/TrackedContractsView";
import FlowTape from "./components/analytics/FlowTape";
import NetGexStrip from "./components/analytics/NetGexStrip";
import RegimeFloatingBadge from "./components/analytics/RegimeFloatingBadge";
import StableFetchProgressBanner from "./components/StableFetchProgressBanner";
import {
    isRegimeBadgeEnabled,
    isFetchProgressBannerEnabled,
} from "./utils/ambientSurfacePrefs";
import AIAnalysisNotification from "./components/ai-chatbot/AIAnalysisNotification";
import ElitePulseReportNotification from "./components/ai-chatbot/ElitePulseReportNotification";
import GoogleAnalytics from "./components/GoogleAnalytics";
import { AppSplash, ViewLoading } from "./components/mmec/MMECLoading";
import {
    STABLE_PROGRESS_MAX_IDLE_POLLS,
    STABLE_PROGRESS_POLL_MS,
    delayMs,
    describeStableProgress,
    stableProgressOf,
    type StableFetchProgress,
} from "./services/stableFetchProgress";

// ── Nav Types — icon is required in both to match MMECNavRail exactly ─────────
export interface NavItem {
    id: string;
    title: string;
    icon: string; // required — MMECNavRail NavItem.icon is not optional
}
export interface NavCategory {
    id: string;
    title: string;
    icon: string;
    items: NavItem[];
}

/** Main nav "Bands" section removed; bookmarks with these ids redirect to Data Table. */
const REMOVED_BAND_TAB_IDS = new Set<string>([
    "haltz-analysis",
    "band-analysis",
    "bands-analysis",
    "zahn-analysis",
    "combined-zahn-belt",
    "belt-ranges",
    "belt-ranges-analysis",
]);

/** Legacy RTL: Volume Delta Reformed and Trades Chart Reformed. */
const LEGACY_VOLUME_REFORMED_TAB_ID = "volume-delta-reformed";

// Lazy-load heavier tabs to reduce initial bundle size.
const DetailedOptionsCharts = lazy(
    () => import("./components/DetailedOptionsCharts"),
);
const IvTrackingTable = lazy(
    () => import("./components/analysis/IvTrackingTable"),
);
const EdgeScannerView = lazy(
    () => import("./components/edge/EdgeScannerView"),
);
const NewsFeedView = lazy(() => import("./components/news/NewsFeedView"));
const MarketClimateView = lazy(
    () => import("./components/edge/MarketClimateView"),
);
const EventOutlookView = lazy(
    () => import("./components/news/EventOutlookView"),
);
const PathCloudView = lazy(() => import("./components/edge/PathCloudView"));
const StructureLabView = lazy(
    () => import("./components/edge/StructureLabView"),
);
const DailyEdgeView = lazy(() => import("./components/edge/DailyEdgeView"));
const ProtectView = lazy(() => import("./components/protect/ProtectView"));
const CryptoView = lazy(() => import("./components/crypto/CryptoView"));
const GammaShiftView = lazy(() => import("./components/edge/GammaShiftView"));
const TodayDashboard = lazy(() => import("./components/dashboard/TodayDashboard"));
const NetDriftView = lazy(() => import("./components/netdrift/NetDriftView"));
const StatementTrackerView = lazy(
    () => import("./components/news/StatementTrackerView"),
);

// ─────────────────────────────────────────────────────────────────────────────
const App: React.FC = () => {
    // ── Contexts ────────────────────────────────────────────────────────────────
    const { isAuthenticated, loading, user } = useAuth();
    const { language } = useLanguage();
    const {
        isFuturesMode,
        selectedFutures,
        filterData,
        convertData,
        getOptionsUnderlyingForFuture,
    } = useSymbolMapping();
    const { generateCalls, refreshAllCallPerformance } = useTradingCall();
    const { dataSource } = useDataSource();
    const { mode: mmecMode } = useMMECTheme();

    void useExperienceMode();

    // ── Admin ────────────────────────────────────────────────────────────────────
    /** Pro sidebar Tools (calls / tracker / widget / generator): g.althawr@gmail.com only — not `user.isAdmin`. */
    const canUseMmecTradingToolsNav = useMemo(
        () => isMmecToolsRailUser(user),
        [user],
    );

    // ── Data Mode ─────────────────────────────────────────────────────────────────
    const [dataMode, setDataMode] = useState<"5min" | "accumulated">(
        "accumulated",
    );

    // ── Persist Mode (RTL legacy only) ────────────────────────────────────────────
    const [persistMode, setPersistMode] = useState<"" | "1" | "yesterday">("");

    // ── Scanner Data ──────────────────────────────────────────────────────────────
    const dataRef = useRef<ScannerData[]>([]);
    const [rawData, setRawData] = useState<ScannerData[]>([]);
    const data = useMemo(() => {
        if (!isFuturesMode || !selectedFutures.length) return rawData;
        const filtered = filterData(rawData);
        return convertData(filtered) as ScannerData[];
    }, [rawData, isFuturesMode, selectedFutures, filterData, convertData]);

    const {
        snapshot: marketRegimeSnapshot,
        regime: marketRegime,
        regimeEmoji,
        regimeLabel,
    } = useRegime();

    const setData = useMemo(
        () => (d: ScannerData[] | ((prev: ScannerData[]) => ScannerData[])) =>
            setRawData(d),
        [],
    );

    // ── Desktop mode detection ────────────────────────────────────────────────────
    const isDesktopMode = useMemo(() => {
        try {
            // Primary: preload script flag (always present in desktop app)
            if ((window as any).__MMEC_DESKTOP_MODE === true) return true;
            // Fallback: query param (useful for testing in browser)
            const fromSearch =
                new URLSearchParams(window.location.search).get(
                    "desktop_mode",
                ) === "dashboard";
            if (fromSearch) return true;
            const hash = window.location.hash;
            if (hash && hash.includes("?")) {
                const hashQuery = hash.slice(hash.indexOf("?"));
                return (
                    new URLSearchParams(hashQuery).get("desktop_mode") ===
                    "dashboard"
                );
            }
            return false;
        } catch {
            return false;
        }
    }, []);

    // ── UI State ──────────────────────────────────────────────────────────────────
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    // `?tab=<id>` opens that tab; retired ids then pass through TAB_REDIRECTS below, so an old
    // link such as ?tab=smart-flow-scanner lands on its replacement.
    const [activeComponent, setActiveComponent] = useState(() => {
        try {
            const fromUrl = new URLSearchParams(window.location.search).get("tab");
            if (fromUrl && /^[a-z0-9-]{2,60}$/.test(fromUrl)) return fromUrl;
        } catch {
            /* no URL access: fall through to the default */
        }
        return isDesktopMode ? "dashboard-mode" : "data-table";
    });

    /** AI chat: collapsed = floating launcher only. On simple/mobile, default to collapsed. */
    const [trackingPanelOpen, setTrackingPanelOpen] = useState(false);
    const [aiChatCollapsed, setAiChatCollapsed] = useState(() => {
        try {
            if (mmecMode === "simple") return true;
            // Collapsed by default: expanded it covers the filter rail. Only a
            // person who opened it (stored "0") gets it open on the next load.
            return localStorage.getItem("mmec_ai_chat_collapsed") !== "0";
        } catch {
            return mmecMode === "simple";
        }
    });
    const toggleAiChatCollapsed = () => {
        setAiChatCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(
                    "mmec_ai_chat_collapsed",
                    next ? "1" : "0",
                );
            } catch {
                /* ignore */
            }
            return next;
        });
    };

    void dashboardLoading;
    void lastUpdate;

    // ── Legal Disclaimer (per browser session; cleared when the tab/session ends) ──
    const LEGAL_SESSION_KEY = "mmec-legal-disclaimer-accepted";
    const [hasAcceptedLegalDisclaimer, setHasAcceptedLegalDisclaimer] =
        useState(() => sessionStorage.getItem(LEGAL_SESSION_KEY) === "true");
    const handleAcceptLegalDisclaimer = () => {
        try {
            sessionStorage.setItem(LEGAL_SESSION_KEY, "true");
            // The platform tour no longer auto-starts. It launches only when the
            // user asks for it (the "Start tour" controls call startTour() directly).
        } catch {
            /* ignore */
        }
        setHasAcceptedLegalDisclaimer(true);
        window.dispatchEvent(new CustomEvent("mmec-legal-accepted"));
    };
    const handleDenyLegalDisclaimer = () => {
        window.close();
    };

    // ── Filters ───────────────────────────────────────────────────────────────────
    const [selectedUnderlying] = useState(() => {
        try {
            const ds = localStorage.getItem("mmec_data_source");
            if (ds === "stable") {
                const sym = readStablePrimarySessionFirst();
                if (sym?.trim()) return sym.trim();
            }
        } catch (_) {}
        return "all";
    });

    // ── Fetch Refs ────────────────────────────────────────────────────────────────
    const stableFetchAbortRef = useRef<AbortController | null>(null);
    const dataLoadRequestIdRef = useRef(0);
    // Bumped by every loadData call (not by the 30s background refresh), so a newer fetch
    // stops an older one's expiry polling without the refresh cutting it short.
    const stableProgressPollRef = useRef(0);
    const suppressStableAutoEventsUntilRef = useRef(0);

    usePageId("App", `active=${activeComponent} mode=${mmecMode}`);

    /** Simple mode: scanner rule alerts are muted unless user opts in (`mmec_simple_scanner_rules_opt_in`). */
    useEffect(() => {
        try {
            if (mmecMode === "simple") {
                sessionStorage.setItem("mmec_simple_mode_active", "1");
            } else {
                sessionStorage.removeItem("mmec_simple_mode_active");
            }
        } catch {
            /* ignore */
        }
    }, [mmecMode]);

    useEffect(() => {
        const onTourNav = (e: Event) => {
            const id = (e as CustomEvent<{ id?: string }>).detail?.id;
            if (id && typeof id === "string") {
                setActiveComponent(id);
            }
        };
        window.addEventListener(
            "mmec-tour-select-component",
            onTourNav as EventListener,
        );
        return () =>
            window.removeEventListener(
                "mmec-tour-select-component",
                onTourNav as EventListener,
            );
    }, []);

    // ── Data Loading ──────────────────────────────────────────────────────────────

    /** Fast path for RTL legacy: loads instantly from JSON ingest, no loading spinner. */
    const loadRtlLegacyFast = useCallback(
        async (persistOverride?: string) => {
            const requestId = ++dataLoadRequestIdRef.current;
            const mode = persistOverride ?? persistMode;
            try {
                const result = await dataService.fetchRawData(
                    dataMode,
                    undefined,
                    {
                        sourceOverride: "rtl_legacy",
                        persist: mode || undefined,
                    },
                );
                if (requestId !== dataLoadRequestIdRef.current) return;
                if (result?.length) {
                    dataRef.current = result;
                    setData(result);
                    setLastUpdate(new Date());
                    generateCalls(result);
                    refreshAllCallPerformance(result);
                }
            } catch (_) {
                // RTL legacy fast load failed - keep existing data, don't clear
            } finally {
                if (requestId === dataLoadRequestIdRef.current)
                    setDashboardLoading(false);
            }
        },
        [dataMode, persistMode],
    );

    const loadData = async (
        signal?: AbortSignal,
        options?: {
            onStableStatus?: (
                message: string,
                phase: "start" | "success" | "error" | "cancel",
                rows?: number,
                progress?: StableFetchProgress | null,
            ) => void;
            forceRefresh?: boolean;
            underlyingsOverride?: string[];
            sourceOverride?: "rtl_legacy" | "stable";
        },
    ) => {
        const requestId = ++dataLoadRequestIdRef.current;
        const pollToken = ++stableProgressPollRef.current;
        const report = options?.onStableStatus;
        const progressRef = {
            current: null as ReturnType<typeof setInterval> | null,
        };
        const startTime = report ? Date.now() : 0;
        try {
            setDashboardLoading(true);
            if (report) {
                report("Fetching…", "start");
                progressRef.current = setInterval(() => {
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    report(`Still loading… (${elapsed}s)`, "start");
                }, 2000);
            }
            const result = await dataService.fetchRawData(dataMode, signal, {
                forceRefresh: options?.forceRefresh,
                underlyingsOverride: options?.underlyingsOverride,
                sourceOverride: options?.sourceOverride,
            });
            if (progressRef.current) {
                clearInterval(progressRef.current);
                progressRef.current = null;
            }
            if (requestId !== dataLoadRequestIdRef.current) return;
            if (result?.length) {
                dataRef.current = result;
                setData(result);
            } else if (!dataRef.current?.length) {
                dataRef.current = result ?? [];
                setData(result ?? []);
            }
            // Empty refresh while cache exists: keep last good RTL snapshot on screen.
            setLastUpdate(new Date());
            if (result?.length) {
                generateCalls(result);
                refreshAllCallPerformance(result);
            }
            // Ranged stable fetches arrive progressively: show what is in, then poll until
            // every expiry in the range has loaded. Stops on a newer fetch, cancel, or after
            // ~3 min with no new expiry -- never on a fixed attempt count.
            let finalRows = result;
            let progress = stableProgressOf(result);
            if (progress?.partial) {
                setDashboardLoading(false);
                let lastLoaded = progress.loaded;
                let idle = 0;
                while (progress?.partial) {
                    if (signal?.aborted || pollToken !== stableProgressPollRef.current) break;
                    report?.(
                        `Loading expiries: ${describeStableProgress(progress)}…`,
                        "start",
                        finalRows?.length,
                        progress,
                    );
                    try {
                        await delayMs(STABLE_PROGRESS_POLL_MS, signal);
                    } catch {
                        break;
                    }
                    if (pollToken !== stableProgressPollRef.current) break;
                    let more: typeof result;
                    try {
                        more = await dataService.fetchRawData(dataMode, signal, {
                            underlyingsOverride: options?.underlyingsOverride,
                            sourceOverride: options?.sourceOverride,
                        });
                    } catch {
                        idle += 1;
                        if (idle >= STABLE_PROGRESS_MAX_IDLE_POLLS) break;
                        continue;
                    }
                    if (signal?.aborted || pollToken !== stableProgressPollRef.current) break;
                    const next = stableProgressOf(more);
                    if (more?.length && (!next || next.loaded >= lastLoaded)) {
                        finalRows = more;
                        dataRef.current = more;
                        setData(more);
                        setLastUpdate(new Date());
                    }
                    if (!next) break;
                    progress = next;
                    if (next.loaded > lastLoaded) {
                        lastLoaded = next.loaded;
                        idle = 0;
                    } else if (++idle >= STABLE_PROGRESS_MAX_IDLE_POLLS) {
                        break;
                    }
                }
                if (pollToken !== stableProgressPollRef.current) return;
                if (signal?.aborted) {
                    // Keep what loaded on screen; the bar reports the cancel.
                    throw new DOMException("Aborted", "AbortError");
                }
                if (finalRows?.length) {
                    generateCalls(finalRows);
                    refreshAllCallPerformance(finalRows);
                }
            }
            if (report) {
                const expiryNote = progress
                    ? ` ${describeStableProgress(progress)}${
                          progress.partial
                              ? " — the rest are still loading on the server; fetch again shortly."
                              : "."
                      }`
                    : "";
                report(
                    `Loaded ${finalRows.length} rows.${expiryNote}`,
                    "success",
                    finalRows.length,
                    progress,
                );
                window.dispatchEvent(
                    new CustomEvent("stableDataFetched", {
                        detail: {
                            rows: finalRows.length,
                            symbols: options?.underlyingsOverride?.length
                                ? options.underlyingsOverride
                                : readStableUnderlyingsSessionFirst(),
                            underlying: readStablePrimarySessionFirst(),
                        },
                    }),
                );
            }
        } catch (err: any) {
            if (progressRef.current) {
                clearInterval(progressRef.current);
                progressRef.current = null;
            }
            if (report) {
                const isCancel =
                    err?.message === "Cancelled" || err?.name === "AbortError";
                report(
                    isCancel ? "Cancelled" : err?.message || "Request failed",
                    isCancel ? "cancel" : "error",
                );
            }
        } finally {
            if (progressRef.current) clearInterval(progressRef.current);
            if (requestId === dataLoadRequestIdRef.current)
                setDashboardLoading(false);
        }
    };

    // ── Effects ───────────────────────────────────────────────────────────────────

    const futuresKey = selectedFutures.join(",");
    useEffect(() => {
        if (!isFuturesMode || !selectedFutures.length || !isAuthenticated)
            return;
        const underlyings = Array.from(
            new Set(
                selectedFutures
                    .map((f) => getOptionsUnderlyingForFuture(f))
                    .filter(Boolean),
            ),
        );
        if (!underlyings.length) return;
        setStableTabSelection(underlyings);
        loadData(undefined, {
            underlyingsOverride: underlyings,
            sourceOverride: dataSource as any,
        });
    }, [isFuturesMode, futuresKey]); // eslint-disable-line

    useEffect(() => {
        if (!isAuthenticated) {
            setDashboardLoading(false);
            return;
        }
        const RTL_MS = 10000;
        const STABLE_MS = 30000;
        const stablePrimary = readStablePrimarySessionFirst();
        const isRtl = dataSource === "rtl_legacy";

        if (isRtl || stablePrimary) {
            isRtl
                ? loadRtlLegacyFast()
                : loadData(undefined, { sourceOverride: dataSource as any });
        } else {
            setDashboardLoading(false);
        }

        const RTL_WATCHDOG_MS = 30000;
        const rtlLiveInterval = setInterval(() => {
            if (document.visibilityState === "hidden") return;
            if (!shouldFeedLiveMarketData()) return;
            if (dataSource === "rtl_legacy") loadRtlLegacyFast();
        }, RTL_MS);

        const rtlWatchdogInterval = setInterval(() => {
            if (document.visibilityState === "hidden") return;
            if (shouldFeedLiveMarketData()) return;
            if (dataSource === "rtl_legacy") loadRtlLegacyFast();
        }, RTL_WATCHDOG_MS);

        const stableInterval = setInterval(async () => {
            if (document.visibilityState === "hidden") return;
            if (!shouldFeedLiveMarketData()) return;
            const src =
                localStorage.getItem("mmec_data_source") || "rtl_legacy";
            if (src !== "stable") return;
            const priorityList = getPriorityFetchSymbols();
            if (!priorityList.length) return;
            const reqId = ++dataLoadRequestIdRef.current;
            try {
                const fresh = await dataService.fetchRawData(
                    dataMode,
                    undefined,
                    {
                        underlyingsOverride: priorityList,
                        isPriorityRefresh: true,
                        sourceOverride: "stable",
                    },
                );
                if (reqId !== dataLoadRequestIdRef.current || !fresh.length)
                    return;
                const cur = dataRef.current?.length ?? 0;
                if (cur > 0 && fresh.length < cur * 0.5) return;
                dataRef.current = fresh;
                setData(fresh);
                setLastUpdate(new Date());
                window.dispatchEvent(
                    new CustomEvent("stableDataFetched", {
                        detail: {
                            rows: fresh.length,
                            symbols: priorityList,
                            underlying: priorityList[0] || "",
                        },
                    }),
                );
            } catch (_) {}
        }, STABLE_MS);

        return () => {
            clearInterval(rtlLiveInterval);
            clearInterval(rtlWatchdogInterval);
            clearInterval(stableInterval);
        };
    }, [isAuthenticated, dataMode, dataSource]); // eslint-disable-line

    useEffect(() => {
        if (!isAuthenticated) return;
        if (dataSource === "rtl_legacy") {
            loadRtlLegacyFast();
        } else {
            loadData(undefined, { sourceOverride: dataSource as any });
        }
    }, [dataMode]); // eslint-disable-line

    useEffect(() => {
        if (!isAuthenticated) return;
        if (dataSource === "rtl_legacy") {
            loadRtlLegacyFast();
        } else {
            setDashboardLoading(true);
            if (dataSource === "stable") {
                setData([]);
                dataRef.current = [];
                const sel = readStablePrimarySessionFirst().trim();
                if (!sel) {
                    setDashboardLoading(false);
                    return;
                }
            }
            loadData(undefined, { sourceOverride: dataSource as any });
        }
    }, [dataSource, isAuthenticated]); // eslint-disable-line

    /** Legacy RTL Excel-only tools — reset when switching to Stable. */
    const LEGACY_RTL_VOLUME_TAB_IDS = useMemo(
        () => [LEGACY_VOLUME_REFORMED_TAB_ID, "trades-chart-reformed"],
        [],
    );

    useEffect(() => {
        if (
            dataSource !== "rtl_legacy" &&
            LEGACY_RTL_VOLUME_TAB_IDS.includes(activeComponent)
        ) {
            setActiveComponent("data-table");
        }
    }, [dataSource, activeComponent, LEGACY_RTL_VOLUME_TAB_IDS]);

    // Dashboard and fullscreen are now valid navigation targets — no redirect needed

    const ADMIN_ONLY = useMemo(
        () => [
            "posted-calls",
            "trading-calls",
            "trading-call-tracker",
            "trading-calls-widget",
            "trading-call-generator",
        ],
        [],
    );
    useEffect(() => {
        if (!isAuthenticated || canUseMmecTradingToolsNav) return;
        if (ADMIN_ONLY.includes(activeComponent))
            setActiveComponent("data-table");
    }, [
        isAuthenticated,
        canUseMmecTradingToolsNav,
        activeComponent,
        ADMIN_ONLY,
    ]);

    // Removed Spot Gamma / hybrid GEX views; bookmarks land on the GEX Workspace.
    useEffect(() => {
        if (
            activeComponent === "gex-spotgamma-lightweight" ||
            activeComponent === "hybrid-gamma-flow" ||
            activeComponent === "hybrid-gamma-flow-chart" ||
            activeComponent === "spot-gamma" ||
            activeComponent === "mmec-gamma"
        ) {
            setActiveComponent("realtime-gex");
        }
    }, [activeComponent]);

    // Volume Analysis removed; bookmarks open Data Table.
    useEffect(() => {
        if (activeComponent === "volume-analysis") {
            setActiveComponent("data-table");
        }
    }, [activeComponent]);

    // Detailed Volume tab removed; same deep analysis lives under Charts → Options Charts.
    useEffect(() => {
        if (activeComponent === "detailed-volume") {
            setActiveComponent("detailed-options");
        }
    }, [activeComponent]);

    // Strike/Expiry tab removed from Charts; normalize id so nav matches Options Charts.
    useEffect(() => {
        if (activeComponent === "strike-expiry-view") {
            setActiveComponent("detailed-options");
        }
    }, [activeComponent]);

    // FinViz / Square heatmap removed; old links land on Options Charts.
    useEffect(() => {
        if (
            activeComponent === "finviz-heatmap" ||
            activeComponent === "square-heatmap"
        ) {
            setActiveComponent("detailed-options");
        }
    }, [activeComponent]);

    // Consolidated / MMEC Gap / Distribution removed; normalize bookmarks.
    useEffect(() => {
        if (
            activeComponent === "consolidated-analysis" ||
            activeComponent === "mmec-gap" ||
            activeComponent === "pure-distribution"
        ) {
            setActiveComponent("detailed-options");
        }
    }, [activeComponent]);

    useEffect(() => {
        if (activeComponent === "gamma-exposure") {
            setActiveComponent("realtime-gex");
        }
    }, [activeComponent]);

    // Protect is exclusive to the desk accounts (owner, 2026-10-22). Anyone else who lands on
    // it -- a saved tab, ?tab=protect, an event -- goes to the dashboard with a quiet note.
    const protectAllowed = useMemo(
        () => canUseProtect() || userAllowedForProtect(user as any),
        [isAuthenticated, user],
    );
    const [protectNote, setProtectNote] = useState(false);
    useEffect(() => {
        if (activeComponent === "protect" && !protectAllowed && isAuthenticated) {
            setActiveComponent("dashboard-mode");
            setProtectNote(true);
        }
    }, [activeComponent, protectAllowed, isAuthenticated]);
    useEffect(() => {
        if (!protectNote) return;
        const t = window.setTimeout(() => setProtectNote(false), 6000);
        return () => window.clearTimeout(t);
    }, [protectNote]);

    // Retired tabs (saved, linked or fired by an event) open their replacement.
    useEffect(() => {
        const target = TAB_REDIRECTS[activeComponent];
        if (target) setActiveComponent(target);
    }, [activeComponent]);

    // Candlestick tab removed from Charts; old links open Options Charts.
    useEffect(() => {
        if (activeComponent === "candlestick") {
            setActiveComponent("detailed-options");
        }
    }, [activeComponent]);

    // TradingView tab removed from Charts; old links open Options Charts.
    useEffect(() => {
        if (activeComponent === "tradingview-chart") {
            setActiveComponent("detailed-options");
        }
    }, [activeComponent]);

    // Bands section removed; old deep links open Data Table.
    useEffect(() => {
        if (REMOVED_BAND_TAB_IDS.has(activeComponent)) {
            setActiveComponent("data-table");
        }
    }, [activeComponent]);

    useEffect(() => {
        const onUnderlyingChanged = () => {
            if (Date.now() < suppressStableAutoEventsUntilRef.current) return;
            loadData();
        };
        const onExpiryChanged = () => {
            if (Date.now() < suppressStableAutoEventsUntilRef.current) return;
            loadData();
        };
        const onStableFetch = (e: Event) => {
            const ev = e as CustomEvent<{
                underlying?: string;
                expiryRange?: string;
                expiryDate?: string;
                snapshotDate?: string;
                futuresMode?: boolean;
            }>;
            // Manual stable fetch already triggers data reload; suppress the immediate
            // underlying/expiry auto-events from causing a duplicate request burst.
            suppressStableAutoEventsUntilRef.current = Date.now() + 1500;
            // Sync localStorage so dataService.fetchRawData picks up the latest params.
            if (ev?.detail?.expiryRange) {
                localStorage.setItem("mmec_expiry_range", ev.detail.expiryRange);
            }
            if (ev?.detail?.expiryDate) {
                localStorage.setItem("mmec_expiry_date", ev.detail.expiryDate);
            } else if (ev?.detail?.expiryRange !== "specific-date" && ev?.detail?.expiryRange !== "custom") {
                localStorage.removeItem("mmec_expiry_date");
            }
            if (ev?.detail?.snapshotDate) {
                localStorage.setItem("mmec_snapshot_date", ev.detail.snapshotDate);
            } else {
                localStorage.removeItem("mmec_snapshot_date");
            }
            stableFetchAbortRef.current?.abort();
            const ctrl = new AbortController();
            stableFetchAbortRef.current = ctrl;
            const onStatus = (
                msg: string,
                phase: "start" | "success" | "error" | "cancel",
                rows?: number,
                progress?: StableFetchProgress | null,
            ) => {
                window.dispatchEvent(
                    new CustomEvent("stableDataFetchStatus", {
                        detail: { message: msg, phase, rows, progress: progress ?? null },
                    }),
                );
            };
            if (ev?.detail?.futuresMode) {
                const underlyings = readStableUnderlyingsSessionFirst();
                loadData(ctrl.signal, {
                    onStableStatus: onStatus,
                    forceRefresh: true,
                    underlyingsOverride: underlyings,
                    sourceOverride: "stable",
                });
            } else {
                // Pass the symbol from the fetch bar as underlyingsOverride so
                // the backend query targets exactly what the user typed.
                const manualSymbol = ev?.detail?.underlying?.trim();
                loadData(ctrl.signal, {
                    onStableStatus: onStatus,
                    forceRefresh: true,
                    underlyingsOverride: manualSymbol
                        ? [manualSymbol.toUpperCase()]
                        : undefined,
                    sourceOverride: "stable",
                });
            }
        };
        const onStableCancel = () => {
            stableFetchAbortRef.current?.abort();
        };
        const onDataSourceChanged = (e: Event) => {
            const ev = e as CustomEvent<{ source?: string }>;
            loadData(undefined, {
                sourceOverride:
                    (ev?.detail?.source as any) || (dataSource as any),
            });
        };
        const onNavigate = (e: Event) => {
            const ev = e as CustomEvent<{ tab?: string }>;
            if (ev?.detail?.tab) setActiveComponent(ev.detail.tab);
        };

        window.addEventListener("underlyingChanged", onUnderlyingChanged);
        window.addEventListener("expiryRangeChanged", onExpiryChanged);
        window.addEventListener("stableDataFetchRequested", onStableFetch);
        window.addEventListener(
            "stableDataFetchCancelRequested",
            onStableCancel,
        );
        window.addEventListener("dataSourceChanged", onDataSourceChanged);
        window.addEventListener("navigate-to-tab", onNavigate);
        return () => {
            window.removeEventListener(
                "underlyingChanged",
                onUnderlyingChanged,
            );
            window.removeEventListener("expiryRangeChanged", onExpiryChanged);
            window.removeEventListener(
                "stableDataFetchRequested",
                onStableFetch,
            );
            window.removeEventListener(
                "stableDataFetchCancelRequested",
                onStableCancel,
            );
            window.removeEventListener(
                "dataSourceChanged",
                onDataSourceChanged,
            );
            window.removeEventListener("navigate-to-tab", onNavigate);
        };
    }, [dataMode, dataSource]); // eslint-disable-line

    // ── Navigation Categories ─────────────────────────────────────────────────────
    // icon is required on NavItem to match MMECNavRail.NavItem.icon (non-optional)
    const navigationCategories = useMemo<NavCategory[]>(() => {
        const cats: NavCategory[] = [
            ...(protectAllowed
                ? [
                      {
                          id: "protect-group",
                          title: "Protect",
                          icon: "⛨",
                          items: [{ id: "protect", title: "Protect", icon: "⛨" }],
                      },
                  ]
                : []),
            {
                id: "crypto-group",
                title: "Crypto",
                icon: "₿",
                items: [{ id: "crypto", title: "Crypto", icon: "₿" }],
            },
            {
                id: "overview",
                title: "Overview",
                icon: "◉",
                items: [
                    { id: "data-table", title: "Scanner", icon: "⊞" },
                    { id: "elite-pulse", title: "Elite Pulse", icon: "◉" },
                    { id: "dashboard-mode", title: "Dashboard", icon: "▦" },
                    { id: "analytics-hub", title: "Flow Tape", icon: "◎" },
                    { id: "tracked-contracts", title: "Watchlist", icon: "⭐" },
                ],
            },
            {
                id: "flow",
                title: "Flow",
                icon: "⟳",
                items: [
                    { id: "options-flow", title: "Options Tape", icon: "↕" },
                    { id: "flow-heatmap", title: "Flow Heatmap", icon: "▦" },
                    { id: "flow-matrix", title: "Flow Matrix", icon: "▣" },
                ],
            },
            {
                id: "volatility",
                title: "Volatility",
                icon: "σ",
                items: [
                    { id: "iv-tracking", title: "IV Tracking", icon: "◈" },
                ],
            },
            {
                id: "edge",
                title: "Edge Journey",
                icon: "◎",
                items: [
                    { id: "market-climate", title: "Market Climate", icon: "◐" },
                    { id: "edge-scanner", title: "IV Scanner", icon: "◎" },
                    { id: "path-cloud", title: "Path Cloud", icon: "☁" },
                    { id: "structure-lab", title: "Structure Lab", icon: "⚗" },
                    { id: "daily-edge", title: "Daily Edge List", icon: "☑" },
                ],
            },
            {
                id: "news",
                title: "News",
                icon: "▤",
                items: [
                    { id: "news-feed", title: "News Feed", icon: "▤" },
                    { id: "event-outlook", title: "Event Outlook", icon: "◷" },
                    { id: "statement-tracker", title: "Statement Tracker", icon: "◉" },
                ],
            },
            {
                id: "gex",
                title: "GEX",
                icon: "Γ",
                items: [
                    {
                        id: "realtime-gex",
                        title: "GEX Workspace",
                        icon: "⚡",
                    },
                    {
                        id: "volume-oi-spotgamma",
                        title: "Vol/OI/SG",
                        icon: "▣",
                    },
                    {
                        id: "vertical-spotgamma-oi",
                        title: "Vertical SG/OI",
                        icon: "↕",
                    },
                    { id: "gamma-shift", title: "Gamma Shift", icon: "⇌" },
                    { id: "net-drift", title: "Net Drift", icon: "∿" },
                ],
            },
            {
                id: "charts",
                title: "Charts",
                icon: "⬡",
                items: [
                    {
                        id: "detailed-options",
                        title: "Options Charts",
                        icon: "⬡",
                    },
                ],
            },
            {
                id: "tools",
                title: "Tools",
                icon: "⚙",
                items: [
                    {
                        id: "sigma-range-calc",
                        title: "σ Calculator",
                        icon: "σ",
                    },
                    {
                        id: "option-change-tracker",
                        title: "Abu Sultan 2.0 — contract change",
                        icon: "▤",
                    },
                    ...(canUseMmecTradingToolsNav
                        ? [
                              {
                                  id: "posted-calls",
                                  title: "Posted Calls",
                                  icon: "◉",
                              },
                              {
                                  id: "trading-calls",
                                  title: "Trading Calls",
                                  icon: "📈",
                              },
                              {
                                  id: "trading-call-tracker",
                                  title: "Call Tracker",
                                  icon: "◷",
                              },
                              {
                                  id: "trading-calls-widget",
                                  title: "Calls Widget",
                                  icon: "◈",
                              },
                              {
                                  id: "trading-call-generator",
                                  title: "Call Generator",
                                  icon: "⊕",
                              },
                          ]
                        : []),
                ],
            },
        ];

        if (dataSource === "rtl_legacy") {
            cats.push({
                id: "legacy-rtl-volume",
                title: "Legacy RTL",
                icon: "⊞",
                items: [
                    {
                        id: LEGACY_VOLUME_REFORMED_TAB_ID,
                        title: "Volume / Δ / Trades (one page)",
                        icon: "Δ",
                    },
                    {
                        id: "trades-chart-reformed",
                        title: "Trades (reformed)",
                        icon: "≋",
                    },
                ],
            });
        }

        return cats.filter((c) => c.items.length > 0);
    }, [canUseMmecTradingToolsNav, dataSource, protectAllowed]);

    // ── Component Renderer ────────────────────────────────────────────────────────
    const renderLazy = (node: React.ReactNode) => (
        <Suspense
            fallback={<ViewLoading />}
        >
            {node}
        </Suspense>
    );

    const getCurrentComponent = (): React.ReactNode => {
        switch (activeComponent) {
            // ── Overview ──────────────────────────────────────────────────────────────
            case "data-table":
                return <DataTable data={data} />;
            case "first-glance":
                return <MarketAnalysisPage scannerRows={data} underlying={data?.[0]?.Underlying || 'SPX'} />;
            case "elite-pulse":
                return <RtlScannerView />;
            case "dashboard-mode":
                return renderLazy(<TodayDashboard />);
            case "analytics-hub":
                return <FlowTape scannerData={data} />;
            case "tracked-contracts":
                return <TrackedContractsView />;

            // ── Flow ──────────────────────────────────────────────────────────────────
            case "options-flow":
                return <FlowTapeView />;
            case "flow-heatmap":
                return <FlowHeatmap data={data} dataMode={dataMode} />;
            case "flow-matrix":
                return <FlowMatrixView />;

            // ── Volatility ────────────────────────────────────────────────────────────
            case "iv-tracking":
                return renderLazy(<IvTrackingTable />);

            // ── Edge Journey ──────────────────────────────────────────────────────────
            case "edge-scanner":
                return renderLazy(<EdgeScannerView />);
            case "market-climate":
                return renderLazy(<MarketClimateView />);
            case "path-cloud":
                return renderLazy(<PathCloudView />);
            case "structure-lab":
                return renderLazy(<StructureLabView />);
            case "daily-edge":
                return renderLazy(<DailyEdgeView />);

            // ── Protect ───────────────────────────────────────────────────────────────
            case "protect":
                if (!protectAllowed) return null;
                return renderLazy(<ProtectView />);

            // ── Crypto workspace (crypto.rtl-market.com, one-time launch link) ────────
            case "crypto":
                return renderLazy(<CryptoView />);

            // ── News ──────────────────────────────────────────────────────────────────
            case "news-feed":
                return renderLazy(<NewsFeedView />);
            case "event-outlook":
                return renderLazy(<EventOutlookView />);
            case "statement-tracker":
                return renderLazy(<StatementTrackerView />);

            // ── Legacy RTL — Volume Delta & Trades Reformed (Excel scanner only) ──
            case LEGACY_VOLUME_REFORMED_TAB_ID:
                return dataSource === "rtl_legacy" ? (
                    <VolumeDeltaReformed data={data} dataMode={dataMode} />
                ) : null;
            case "trades-chart-reformed":
                return dataSource === "rtl_legacy" ? (
                    <TradesChartReformed data={data} dataMode={dataMode} />
                ) : null;

            // ── GEX ───────────────────────────────────────────────────────────────────
            case "gamma-credit-hunter":
                return <MarketAnalysisPage scannerRows={data} underlying={data?.[0]?.Underlying || 'SPX'} />;
            case "volume-oi-spotgamma":
                return (
                    <>
                        <NetGexStrip />
                        <VolumeOISpotGammaChart data={data} dataMode={dataMode} />
                    </>
                );
            case "vertical-spotgamma-oi":
                return <VerticalSpotGammaVsOIChart data={data} />;
            case "gamma-shift":
                return renderLazy(<GammaShiftView />);
            case "net-drift":
                return renderLazy(<NetDriftView />);
            case "realtime-gex":
                // The TradingView-style workspace; the previous chart remains only as
                // the runtime fallback if the workspace ever throws.
                return (
                    <LiveGexBoundary fallback={<RealTimeGexChart symbol="SPX" />}>
                        <LiveGexWorkspace symbol="SPX" />
                    </LiveGexBoundary>
                );

            // ── Charts ────────────────────────────────────────────────────────────────
            case "detailed-options":
                return renderLazy(
                    <DetailedOptionsCharts data={data} dataMode={dataMode} />,
                );
            case "strike-expiry-view":
                // Tab removed from Charts menu; old links open the same workspace (Options Charts).
                return renderLazy(
                    <DetailedOptionsCharts data={data} dataMode={dataMode} />,
                );
            case "finviz-heatmap":
            case "square-heatmap":
            case "candlestick":
            case "tradingview-chart":
                return renderLazy(
                    <DetailedOptionsCharts data={data} dataMode={dataMode} />,
                );

            // ── Tools ─────────────────────────────────────────────────────────────────
            case "sigma-range-calc":
                return <SigmaRangeCalculator scannerRows={data} />;
            case "option-change-tracker":
                return (
                    <OptionChangeTrackerPage data={data} dataMode={dataMode} />
                );
            case "posted-calls":
                return canUseMmecTradingToolsNav ? <PostedCallsFeed /> : null;
            case "trading-calls":
                return canUseMmecTradingToolsNav ? (
                    <TradingCallSystem scannerData={data} />
                ) : null;
            case "trading-call-tracker":
                // TradingCallTracker: no props
                return canUseMmecTradingToolsNav ? (
                    <TradingCallTracker />
                ) : null;
            case "trading-calls-widget":
                return canUseMmecTradingToolsNav ? (
                    <TradingCallsWidget data={data} dataMode={dataMode} />
                ) : null;
            case "trading-call-generator":
                return canUseMmecTradingToolsNav ? (
                    <TradingCallGeneratorPage
                        scannerData={data}
                        dataMode={dataMode}
                    />
                ) : null;

            default:
                return <DataTable data={data} />;
        }
    };

    // ── AI Chatbot market context: pass current data so the chatbot sees what the user sees ──
    const [analyticsHubAi, setAnalyticsHubAi] = useState(() =>
        getAnalyticsHubAiSnapshot(),
    );

    useEffect(() => {
        return subscribeAnalyticsHubAiSnapshot(() =>
            setAnalyticsHubAi(getAnalyticsHubAiSnapshot()),
        );
    }, []);

    const aiMarketContext = useMemo(
        () => ({
            rawData: data,
            filtered_data: data,
            real_data_sample: data?.slice(0, 50) || [],
            data_source: dataSource,
            dataSource: dataSource,
            currentDataMode: dataMode,
            active_view: activeComponent,
            analytics_hub_context:
                activeComponent === "analytics-hub" && analyticsHubAi
                    ? analyticsHubAi
                    : null,
            regime: marketRegime,
            regime_label: regimeLabel,
            regime_emoji: regimeEmoji,
            risk_level: marketRegimeSnapshot?.risk_level ?? null,
            regime_confidence: marketRegimeSnapshot?.regime_confidence ?? null,
            bl_forward_pct: marketRegimeSnapshot?.rnd?.forward_pct ?? null,
            bl_skewness: marketRegimeSnapshot?.rnd?.skewness ?? null,
            bl_kurtosis: marketRegimeSnapshot?.rnd?.kurtosis ?? null,
            bl_tail_ratio: marketRegimeSnapshot?.rnd?.tail_ratio ?? null,
            bl_regime_skew: marketRegimeSnapshot?.rnd?.regime_skew ?? null,
            gex_trap_zone: marketRegimeSnapshot?.gex?.trap_zone ?? false,
            pin_probability: marketRegimeSnapshot?.gex?.pin_probability ?? null,
            data_fingerprint: data?.length
                ? {
                      record_count: data.length,
                      symbols: Array.from(
                          new Set(
                              (data || [])
                                  .map((r: any) => r?.Underlying || "")
                                  .filter(Boolean),
                          ),
                      ).slice(0, 30),
                  }
                : null,
        }),
        [
            data,
            dataSource,
            dataMode,
            activeComponent,
            analyticsHubAi,
            marketRegime,
            regimeLabel,
            regimeEmoji,
            marketRegimeSnapshot,
        ],
    );

    // ── Render ────────────────────────────────────────────────────────────────────

    // Desktop mode: bypass login and legal disclaimer (auth handled via Electron IPC).
    // Render the FULL app with navigation so all tools are accessible, not just DashboardMode.
    const effectiveIsAuthenticated = isDesktopMode || isAuthenticated;
    const effectiveHasAcceptedLegal =
        isDesktopMode || hasAcceptedLegalDisclaimer;

    if (loading) {
        return <AppSplash />;
    }

    if (!effectiveIsAuthenticated) {
        return <LoginScreen />;
    }

    if (!effectiveHasAcceptedLegal) {
        return (
            <LegalDisclaimer
                onAccept={handleAcceptLegalDisclaimer}
                onDeny={handleDenyLegalDisclaimer}
            />
        );
    }

    // ── Simple Mode ───────────────────────────────────────────────────────────────
    if (mmecMode === "simple") {
        return (
            <>
                <GoogleAnalytics />
                <MMECSimpleShell data={data} dataMode={dataMode} />
                <MMECCompanion
                    isCollapsed={aiChatCollapsed}
                    onToggle={toggleAiChatCollapsed}
                    marketContext={aiMarketContext}
                />
                <AIAnalysisNotification />
                <ElitePulseReportNotification />
                <AlertToasts />
                <StableFetchAlerts data={data} />
                <CompanionTourLauncher />
                {isRegimeBadgeEnabled() && <RegimeFloatingBadge />}
                {isFetchProgressBannerEnabled() && <StableFetchProgressBanner />}
                <VirtualTour />
            </>
        );
    }

    // ── Advanced Mode ─────────────────────────────────────────────────────────────
    return (
        <>
            <GoogleAnalytics />
            <MMECShell
                categories={navigationCategories}
                activeId={activeComponent}
                onSelect={setActiveComponent}
            >
                {dataSource === "rtl_legacy" && (
                    <div
                        className="mmec-ds-persist"
                        style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "center",
                            padding: "8px 12px 0",
                        }}
                    >
                        <span className="mmec-eyebrow">Persist</span>
                        <div
                            className="mmec-seg"
                            role="group"
                            aria-label="Persist mode"
                            style={
                                {
                                    "--n": 3,
                                    "--i": ["", "1", "yesterday"].indexOf(persistMode),
                                } as React.CSSProperties
                            }
                        >
                            {[
                                { v: "", label: "Off" },
                                { v: "1", label: "Intraday" },
                                { v: "yesterday", label: "+Yesterday" },
                            ].map(({ v, label }) => (
                                <button
                                    key={v}
                                    type="button"
                                    className={`mmec-seg-btn${persistMode === v ? " is-active" : ""}`}
                                    aria-pressed={persistMode === v}
                                    onClick={() => {
                                        setPersistMode(v as "" | "1" | "yesterday");
                                        loadRtlLegacyFast(v || undefined);
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {protectNote && (
                    <div
                        role="status"
                        dir={language === "ar" ? "rtl" : "ltr"}
                        onClick={() => setProtectNote(false)}
                        style={{
                            margin: "8px 12px 0",
                            padding: "8px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--line, rgba(255,255,255,0.12))",
                            background: "var(--elev, rgba(255,255,255,0.04))",
                            color: "var(--muted, #9aa3b2)",
                            fontSize: 13,
                            cursor: "pointer",
                        }}
                    >
                        {language === "ar"
                            ? "هذه الصفحة غير مفعّلة لحسابك"
                            : "This page is not enabled for your account"}
                    </div>
                )}
                {getCurrentComponent()}
            </MMECShell>
            <MMECCompanion
                isCollapsed={aiChatCollapsed}
                onToggle={toggleAiChatCollapsed}
                marketContext={aiMarketContext}
            />
            <AIAnalysisNotification />
            <ElitePulseReportNotification />
            {/* Advanced mode needs these as much as simple mode: alerts raised anywhere must be
                visible wherever the user is, and the stable-fetch rules must be reachable. */}
            <AlertToasts />
            <StableFetchAlerts data={data} />
            <CompanionTourLauncher />
            {isRegimeBadgeEnabled() && <RegimeFloatingBadge />}
            {isFetchProgressBannerEnabled() && <StableFetchProgressBanner />}
            <VirtualTour />
            <TrackedContractsPanel
                isOpen={trackingPanelOpen}
                onClose={() => setTrackingPanelOpen(false)}
                colors={{
                    surface: "var(--elev, #1a1a2e)",
                    surfaceBorder: "var(--hair, #2a2a40)",
                    background: "var(--bg, #0a0a0a)",
                    text: "var(--fg, #e0e0e0)",
                    textMuted: "var(--dim, #888)",
                    textSecondary: "var(--dim, #888)",
                    primary: "var(--acc, #45a29e)",
                    success: "#22c55e",
                    error: "#ef4444",
                }}
            />
        </>
    );
};

export default App;
